/**
 * Runtime configuration loaded from /config.json (written by the nginx
 * entrypoint script from container env vars).
 *
 * Load once at app bootstrap (see main.tsx). Subsequent reads via
 * `getConfig()` are synchronous.
 *
 * In dev, /config.json doesn't exist; we fall back to a hard-coded
 * dev-defaults object so `npm run dev` keeps working. Container builds
 * always serve a real /config.json.
 */

export interface AppConfig {
  apiBaseUrl: string;
  auth0: {
    domain: string;
    clientId: string;
    audience: string;
  };
  features: {
    /**
     * F-G1: when true, the SPA's /login + /signup routes immediately call
     * loginWithRedirect() instead of rendering the brand-styled in-app
     * cards. Flipping back to false restores the legacy UI without a code
     * change. Only this flag reaches the frontend; the gate + email flags
     * stay server-side.
     */
    legacyLoginPageDisabled: boolean;
  };
  /**
   * Honeybadger browser error reporting. Optional: absent (or an empty
   * apiKey) disables reporting — that's the local-dev default. The
   * container entrypoint writes this from HONEYBADGER_API_KEY /
   * HONEYBADGER_ENVIRONMENT. The browser key is public by design.
   */
  honeybadger?: {
    apiKey: string;
    environment: string;
  };
}

const DEV_FALLBACK: AppConfig = {
  apiBaseUrl: 'http://localhost:5079',
  auth0: {
    domain: 'auth-dev.uncomplicatedinc.com',
    clientId: '',
    audience: 'https://api.growthcommons.dev',
  },
  features: {
    // Default ON in dev fallback so local dev matches deployed default.
    legacyLoginPageDisabled: true,
  },
  // No apiKey locally → Honeybadger reporting stays off in `npm run dev`.
  honeybadger: { apiKey: '', environment: 'development' },
};

let loaded: AppConfig | undefined;

export async function loadConfig(): Promise<AppConfig> {
  if (loaded) return loaded;
  try {
    const resp = await fetch('/config.json', { cache: 'no-store' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    loaded = (await resp.json()) as AppConfig;
  } catch {
    // Vite dev server doesn't ship /config.json; fall back so local dev
    // keeps working. Container deploys always serve a real /config.json
    // written by the entrypoint script, so this branch is dev-only.
    if (import.meta.env.DEV) {
      loaded = DEV_FALLBACK;
    } else {
      throw new Error('Failed to load /config.json — required for production builds');
    }
  }
  return loaded;
}

export function getConfig(): AppConfig {
  if (!loaded) {
    throw new Error('Config not loaded yet — await loadConfig() before getConfig()');
  }
  return loaded;
}
